<template>
  <div class="flex flex-col h-[calc(100vh-2rem)] space-y-2 md:space-y-2 p-2 md:p-2.5 w-full">
    <!-- Content Box -->
    <div class="flex-1 bg-white rounded-xl shadow-sm flex flex-col overflow-hidden relative border border-gray-100">

      <!-- Page Header (opt-in: renders when icon, description, or actions slot is provided) -->
      <div v-if="showPageHeader"
        class="px-3 md:px-2.5 py-2 border-b border-gray-100 flex items-center justify-between gap-3 flex-wrap">
        <div class="min-w-0 flex items-center gap-2">
          <div v-if="icon"
            v-tooltip.bottom="description"
            class="w-7 h-7 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0 cursor-help">
            <i :class="'pi ' + icon" class="text-sm"></i>
          </div>
          <h1 class="text-sm md:text-base font-semibold text-gray-800 leading-tight truncate">{{ title }}</h1>
        </div>
        <div v-if="$slots.actions" class="flex items-center gap-2 flex-wrap">
          <slot name="actions" />
        </div>
      </div>

      <!-- Tabs (Sticky) -->
      <div class="sticky top-0 bg-white z-10 border-b border-gray-100">
        <div class="flex overflow-x-auto items-center">
          <template v-for="(tab, index) in tabs" :key="index">
            <div v-if="index > 0 && tab.group && tab.group !== tabs[index - 1]?.group"
              class="h-5 w-px bg-gray-200 mx-1 shrink-0" />
            <button @click="setActiveTab(index)" :class="{
              'text-primary font-semibold border-b-2 border-primary': activeTab === index,
              'text-gray-400': activeTab !== index,
            }"
              :data-testid="`tab-${tab.name.toLowerCase().replace(/\\s+/g, '-')}`"
              class="px-3 md:px-3 py-2.5 md:py-3 whitespace-nowrap transition-colors
                cursor-pointer hover:bg-gray-50 flex items-center gap-1.5 text-xs md:text-sm"
            >
              <i v-if="tab.icon" :class="'pi ' + tab.icon" class="text-xs" />
              {{ tab.name }}
              <Badge v-if="tab.badge !== undefined && tab.badge !== null && tab.badge !== ''"
                :value="String(tab.badge)"
                :severity="activeTab === index ? undefined : 'secondary'"
                class="!text-[10px] !min-w-5 !h-5" />
            </button>
          </template>
        </div>
      </div>

      <div class="p-2 md:p-2.5 overflow-y-auto flex-1">
        <component :is="tabs[activeTab]!.component"></component>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">

interface Tab {
  name: string;
  component: Component;
  icon?: string;
  group?: string;
  badge?: string | number | null;
}

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    default: '',
  },
  icon: {
    type: String,
    default: '',
  },
  tabs: {
    type: Array as PropType<Tab[]>,
    required: true,
  },
  activeTab: {
    type: Number,
    required: true,
  },
  setActiveTab: {
    type: Function as PropType<(_index: number) => void>,
    required: true,
  },
  toggleSidebar: {
    type: Function as PropType<() => void>,
    required: true,
  },
});

const slots = useSlots();
const showPageHeader = computed(
  () => Boolean(props.icon || props.description || slots.actions),
);
</script>

<style scoped>
.overflow-x-auto::-webkit-scrollbar {
  height: 0;
}
</style>
